import React, { useEffect, useState } from "react";

import { addShapeProcessor } from "utils";

import { AdminHead, Button, Card, MainPage, Spin, Tabs, TagNormal, Typography, Upload, Message as message } from "components";

import { AllShapefiles } from "pages/admin/experiments/cropExtraction/AllShapefiles";
import { ExtractionResultView } from "pages/admin/experiments/cropExtraction/components/ExtractionResultView";
import { extractCropsFromZip } from "pages/admin/experiments/cropExtraction/extractCrops";

const MAX_FILE_SIZE_MB = 50;

const SUPPORTED_VENDORS = [
  "data-experts AntDV (5+ regions)",
  "FIONA (Baden-Württemberg)",
  "ELAN-RLP (Rheinland-Pfalz)",
  "ELAN-NRW (Nordrhein-Westfalen)",
  "ANDI (Mecklenburg-Vorpommern / Niedersachsen)",
  "iBALIS (Bayern / Baden-Württemberg)",
  "DIANAweb (Sachsen / Saarland)",
  "InVeKoS-Online Export_*_Shape (Saarland / SH)",
  "ELAN-Hessen (Antragsschläge_POLYGONE)",
];

const UploadOne = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    addShapeProcessor();
  }, []);

  const handleFile = async (file) => {
    if (!file?.name?.toLowerCase().endsWith(".zip")) {
      message({ content: "Only .zip uploads are accepted.", icon: "gremlin" });
      return false;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message({ content: `Files over ${MAX_FILE_SIZE_MB} MB are rejected.`, icon: "gremlin" });
      return false;
    }
    setFileName(file.name);
    setResult(null);
    setLoading(true);
    try {
      const extraction = await extractCropsFromZip(file);
      setResult(extraction);
      if (extraction.success) {
        message({ content: `Extracted ${extraction.extraction.cropCount} crops from ${file.name}`, icon: "gremlin" });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      message({ content: `Extraction failed: ${err?.message ?? "unknown error"}`, icon: "gremlin" });
    } finally {
      setLoading(false);
    }
    return false;
  };

  return (
    <div className="content">
      <Card className="margin-bottom-200">
        <Upload name="file" showUploadList={false} action="" beforeUpload={handleFile} className="auto-height">
          <Button type="primary">Upload zip</Button>
        </Upload>
      </Card>

      {loading && (
        <Card className="margin-bottom-200" style={{ textAlign: "center", padding: "2rem 0" }}>
          <Spin />
          <Typography variant="paragraph-m" style={{ marginTop: "1rem" }}>
            Extracting crops…
          </Typography>
        </Card>
      )}

      {!loading && !result && (
        <Card className="margin-bottom-200">
          <Typography variant="h4" style={{ margin: "0 0 0.5rem 0" }}>
            How it works
          </Typography>
          <Typography variant="paragraph-s">
            Drop a single ZIP exported from any of the supported portals. We classify the format, extract crops in the browser, and show the result as a table —
            nothing is stored.
          </Typography>
          <Typography variant="paragraph-s-bold" style={{ marginTop: "1rem" }}>
            Supported formats
          </Typography>
          <ul style={{ margin: "0.25rem 0 0 1.25rem" }}>
            {SUPPORTED_VENDORS.map((v) => (
              <li key={v}>
                <Typography variant="paragraph-s">{v}</Typography>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {!loading && result && (
        <>
          {fileName && (
            <Typography variant="paragraph-s" color="grey-600" style={{ marginBottom: "0.5rem" }}>
              File: {fileName}
            </Typography>
          )}
          <ExtractionResultView result={result} />
        </>
      )}
    </div>
  );
};

export const CropExtraction = () => {
  const tabItems = [
    { label: "Upload one file", key: "upload", children: <UploadOne /> },
    { label: "Browse all shapefiles", key: "browse", children: <AllShapefiles /> },
  ];

  return (
    <MainPage pageName="admin">
      <AdminHead
        headline={
          <>
            Crop extraction <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />
          </>
        }
        description="Detect the portal vendor and extract field name, field ID, size and crop code. Either upload a single ZIP, or browse every shapefile already in the system and extract any one of them on demand."
      />
      <Tabs defaultActiveKey="upload" size="small" items={tabItems} />
    </MainPage>
  );
};

export default CropExtraction;
